// Does a resolution object carry a RENDERABLE report body?
//
// The distinction matters because two very different objects both satisfy
// "a resolution for this attempt":
//
//   • THIN  — `{status, resolved_at, resolution_date}`. This is what the wire's
//     `active.resolution` (server_ctx, stamped by the notifier's resolved path)
//     carries: the verdict and its dates, nothing else. It is enough to paint the
//     timeline marker, and NOT enough to render the RESOLUTION section.
//   • FULL  — the same, plus `report.{reasoning, key_evidence, confidence,
//     open_reasons, missing_evidence}`. This is what /api/resolve/report returns
//     and what applyPointerOverrides merges onto the live render source.
//
// `buildReforecastEntries` reconciles the newest resolution entry against
// `active.resolution` and stamps it as that entry's `snapshot`. Because the wire
// copy is THIN, the tail resolution entry ends up carrying a bodyless snapshot —
// which then shadowed the pointer-fetched FULL resolution in the report modal
// (`scrubResolutionSnapshot || liveSource.resolution`), so a resolved contract
// rendered its verdict and date with no rationale and no evidence, even though
// the full report had already been fetched and sat one line away.
//
// Treating a bodyless snapshot as "not a snapshot" restores the intended
// fall-through to the fetched overlay, without loosening any fetch gate or
// adding a read. Kept as a pure named predicate (not an inline truthiness check)
// so the thin-vs-full distinction is testable and stays visible at both call
// sites — App.jsx's snapshot selection and the modal's unavailable-body note.

// The terminal verdicts. Mirrors the resolver's own CONCLUSIVE_STATUSES
// (observatory.panel_resolve.bq_io) and the server-side set in
// server/lib/resolutionPointer.js. `unresolved` is settled but NOT conclusive: the
// resolver ran and could not decide. A `voided` verdict is conclusive — the question will
// never have an outcome, and its reasoning says why.
export const CONCLUSIVE_RESOLUTION_STATUSES = [
  "resolved_yes",
  "resolved_no",
  "resolved_tie",
  "voided",
];

/**
 * True for a terminal verdict (`resolved_yes` / `resolved_no` / `resolved_tie` / `voided`).
 *
 * Load-bearing on the read path: `/api/resolve/report` returns the best SETTLED
 * attempt, preferring a conclusive verdict but falling back to an `unresolved` one.
 * A consumer that overlays that payload has to know which it got — an unresolved
 * attempt must never displace a conclusive verdict already in hand.
 *
 * @param {string|null|undefined} status a resolution status
 * @returns {boolean}
 */
export function isConclusiveResolutionStatus(status) {
  return CONCLUSIVE_RESOLUTION_STATUSES.includes(status);
}

/**
 * True when `resolution` carries report content worth rendering — any of the
 * resolver's narrative fields. A verdict + dates alone is NOT a body.
 *
 * Defensive about shape: `report` may be absent (thin wire copy), null, or a
 * non-object; array fields may arrive as non-arrays from a degraded read. Any
 * of those reduce to `false` rather than throwing.
 *
 * @param {object|null|undefined} resolution a resolution object (wire or fetched)
 * @returns {boolean} whether the RESOLUTION section has something to say
 */
export function resolutionHasBody(resolution) {
  if (!resolution || typeof resolution !== "object") return false;
  const r = resolution.report;
  if (!r || typeof r !== "object") return false;
  if (typeof r.reasoning === "string" && r.reasoning.trim().length > 0) return true;
  // The evidence/diagnostic arrays each independently justify rendering the
  // section: an `unresolved` verdict has no `reasoning` but does carry
  // open_reasons / missing_evidence explaining why it could not be decided.
  for (const key of ["key_evidence", "open_reasons", "missing_evidence"]) {
    if (Array.isArray(r[key]) && r[key].length > 0) return true;
  }
  return false;
}
